"use client"

import React, { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useMusicContext } from "@/context/musicContext"
import { useNativeVibration } from "@/hooks/useNativeVibration"
import * as Icons from "../icons"

type Props = {
  className?: string
  onPlay?: () => void
  active?: boolean
  onClick?: () => void
}

export default function PlayButton({
  className = "",
  onPlay,
  active,
  onClick,
}: Props) {
  const {
    isPlaying,
    isLoading,
    togglePlayPause,
    currentTrackData,
  } = useMusicContext()

  const vibrate = useNativeVibration()
  const [isProcessing, setIsProcessing] = useState(false)

  const isRadio = currentTrackData?.type === "radio"
  const playing = active !== undefined ? active : isPlaying

  useEffect(() => {
    if (isPlaying) {
      setIsProcessing(false)
    }
  }, [isPlaying])

  useEffect(() => {
    if (!isLoading && !isPlaying) {
      setIsProcessing(false)
    }
  }, [isLoading, isPlaying])

  const loading = isProcessing && !playing
  const visualState = loading
    ? "loading"
    : playing
      ? isRadio
        ? "stop"
        : "pause"
      : "play"

  const Icon =
    visualState === "loading"
      ? Icons.Loader
      : visualState === "stop"
        ? Icons.Stop
        : visualState === "pause"
          ? Icons.Pause
          : Icons.Play

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    vibrate("light")

    if (onClick) {
      onClick()
      return
    }

    if (playing) {
      togglePlayPause()
      return
    }

    if (onPlay) {
      setIsProcessing(true)
      onPlay()
      return
    }

    setIsProcessing(true)
    togglePlayPause()
  }

  const ariaLabel =
    visualState === "loading"
      ? "Cargando"
      : visualState === "pause"
        ? "Pausar"
        : visualState === "stop"
          ? "Detener radio"
          : "Reproducir"

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      className={`
        relative
        flex
        items-center
        justify-center
        rounded-full
        select-none
        active:scale-95
        transition-transform
        duration-150
        ease-out
        ${className}
      `}
      style={{
        width: "3rem",
        height: "3rem",
        minWidth: "3rem",
        minHeight: "3rem",
        aspectRatio: "1 / 1",
      }}
    >
      <motion.div
        key={visualState}
        initial={{ scale: 0.85, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.18,
          ease: [0.2, 0.8, 0.2, 1],
        }}
      >
        <Icon
          className={`
            w-6
            h-6
            text-[var(--primary-color)]
            fill-white
            ${visualState === "loading" ? "animate-spin" : ""}
          `}
        />
      </motion.div>

      <span
        className="
          absolute
          inset-0
          rounded-full
          bg-white/10
          scale-0
          opacity-0
          active:scale-100
          active:opacity-100
          transition-all
          duration-150
        "
      />
    </button>
  )
}